import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, makeStyles, MenuItem, OutlinedInput, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { Add, Close, Delete, Edit } from '@material-ui/icons';

import { languageCodeToNameMap } from '../../../constants/languageConstants';
import { createDictionary, getAllDictionaries, updateDictionary } from '../../../actions/profileActions';
import { showMessage } from '../../../actions/globalActions';

const styles = makeStyles({
    table: {
        minWidth: 650,
        boxShadow: 'none',
    },
    tableCell: {
        fontWeight: 'bold',
        fontSize: '12px',
    },
    tableCellBody: {
        fontSize: '12px',
    },
    tableCellActions: {
        fontWeight: 'bold',
        fontSize: '12px',
        width: '120px',
    },
    container: {
        maxHeight: 'calc(100% - 100px)',
    },
    input: {
        height: '30px',
        marginBottom: '8px',
    }
});

const CustomDictionary = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const classes = styles();

    const { defaultAccountId } = useSelector((state: any) => state.session);

    const [openAddNewDialog, setOpenAddNewDialog] = React.useState(false);
    const [selectedDictionary, setSelectedDictionary] = React.useState("");

    const [formLanguage, setFormLanguage] = React.useState("");
    const [formWordInput, setFormWordInput] = React.useState("");
    const [formSoundsLike, setFormSoundsLike] = React.useState("");
    const [dictionaries, setDictionaries] = React.useState<any[]>([]);

    const currentDictionary = dictionaries.find((dict: any) => dict.languageCode === selectedDictionary);

    const fetchDictionaryData = useCallback(() => {
        dispatch(getAllDictionaries(defaultAccountId) as any).then((data: any) => {
            if (!data || data.length === 0) {
                setDictionaries([]);
                return;
            }
            setDictionaries(data);
            setSelectedDictionary(data[0].languageCode);
            setFormLanguage(data[0].languageCode);
        });
    }, [dispatch, defaultAccountId]);

    const handleSaveChanges = useCallback(() => {
        if (!formLanguage || !formWordInput) {
            return;
        }

        const newEntry = {
            content: formWordInput,
            sounds_like: formSoundsLike ? formSoundsLike.split(',').map(s => s.trim()) : [],
        };

        let updatedDictionary;
        const existingDictionary = dictionaries.find((dict: any) => dict.languageCode === formLanguage) as any;
        if (existingDictionary) {
            const existingEntryIndex = existingDictionary.entries.findIndex((e: any) => e.content === formWordInput);

            if (existingEntryIndex !== -1) {
                // Update existing entry
                const updatedEntries = [...existingDictionary.entries];
                updatedEntries[existingEntryIndex] = newEntry;
                updatedDictionary = {
                    ...existingDictionary,
                    entries: updatedEntries,
                };
            } else {
                // Add new entry
                updatedDictionary = {
                    ...existingDictionary,
                    entries: [...existingDictionary.entries, newEntry],
                };
            }

            dispatch(updateDictionary(defaultAccountId, formLanguage, updatedDictionary) as any).then(() => {
                dispatch(showMessage(t('SETTING_CUSTOM_DICTIONARY') + ' ' + t('COMMON_UPDATED_SUCCESSFULLY')) as any);
            });
        } else {
            updatedDictionary = {
                languageCode: formLanguage,
                entries: [newEntry],
            };
            dispatch(createDictionary(defaultAccountId, updatedDictionary) as any).then(() => {
                dispatch(showMessage(t('SETTING_CUSTOM_DICTIONARY') + ' ' + t('COMMON_CREATED_SUCCESSFULLY')) as any);
            });
        }

        const updatedDictionaries = dictionaries.filter((dict: any) => dict.languageCode !== formLanguage);
        (updatedDictionaries as any).push(updatedDictionary);
        setDictionaries(updatedDictionaries);

        setOpenAddNewDialog(false);
        setFormWordInput("");
        setFormSoundsLike("");
        setFormLanguage("");
    }, [formLanguage, formWordInput, formSoundsLike, dictionaries, dispatch, defaultAccountId, t]);

    const handleEditButtonClick = useCallback((entry: { content: string; sounds_like?: string[] }) => {
        setFormWordInput(entry.content);
        setFormSoundsLike((entry.sounds_like ?? []).join(', '));
        setFormLanguage(selectedDictionary);
        setOpenAddNewDialog(true);
    }, [selectedDictionary]);

    const handleDeleteButtonClick = useCallback((entry: { content: string; sounds_like?: string[] }) => {
        const updatedEntries = (currentDictionary as any).entries.filter((e: any) => e.content !== entry.content);
        const updatedDictionary = {
            ...(currentDictionary as any),
            entries: updatedEntries,
        };

        dispatch(updateDictionary(defaultAccountId, selectedDictionary, updatedDictionary) as any).then(() => {
            dispatch(showMessage(t('SETTING_CUSTOM_DICTIONARY') + ' ' + t('COMMON_UPDATED_SUCCESSFULLY')) as any);
        });

        const updatedDictionaries = dictionaries.map((dict: any) => {
            if (dict.languageCode === selectedDictionary) {
                return updatedDictionary;
            }
            return dict;
        });
        setDictionaries(updatedDictionaries);

    }, [currentDictionary, dispatch, defaultAccountId, selectedDictionary, dictionaries, t]);

    React.useEffect(() => {
        fetchDictionaryData();
    }, [fetchDictionaryData]);

    return (
        <div className='profileScreen__contentWrapper'>
            <div className='profileScreen__contentHeader'>
                <h3>{t('SETTING_CUSTOM_DICTIONARY')}</h3>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => setOpenAddNewDialog(true)}
                >
                    {t('COMMON_NEW')}
                </Button>
            </div>

            <div>
                <FormControl>
                    <Select
                        value={selectedDictionary}
                        onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                            setSelectedDictionary(e.target.value as string);
                            setFormLanguage(e.target.value as string);
                        }}
                        displayEmpty
                        variant='outlined'
                        className={classes.input}
                    >
                        <MenuItem value="">{t('SETTING_CUSTOM_DICTIONARY_CHOOSE_DICTIONARY') as string}</MenuItem>
                        {dictionaries.map((dictionary: any) => (
                            <MenuItem key={dictionary.languageCode} value={dictionary.languageCode}>
                                {(languageCodeToNameMap as any)[dictionary.languageCode]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            <TableContainer className={classes.container}>
                <Table stickyHeader className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableCell}>{t('SETTING_CUSTOM_DICTIONARY_WORD')}</TableCell>
                            <TableCell className={classes.tableCell}>{t('SETTING_CUSTOM_DICTIONARY_SOUNDS_LIKE')}</TableCell>
                            <TableCell className={classes.tableCellActions}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(currentDictionary as any)?.entries.map((entry: any, index: number) => (
                            <TableRow key={entry.content + index}>
                                <TableCell className={classes.tableCellBody}>{entry.content}</TableCell>
                                <TableCell className={classes.tableCellBody}>
                                    {(entry.sounds_like ?? []).join(', ')}
                                </TableCell>
                                <TableCell className={classes.tableCellActions}>
                                    <IconButton title={t('SETTING_CUSTOM_DICTIONARY_EDIT_LABEL')} onClick={() => handleEditButtonClick(entry)}><Edit color='primary'/></IconButton>
                                    <IconButton title={t('SETTING_CUSTOM_DICTIONARY_DELETE_LABEL')} onClick={() => handleDeleteButtonClick(entry)}><Delete color="error"/></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openAddNewDialog} onClose={() => setOpenAddNewDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle className="dialogTitle" disableTypography>
                    <Typography variant="h6">{t('SETTING_CUSTOM_DICTIONARY')}</Typography>
                    <IconButton onClick={() => setOpenAddNewDialog(false)}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth>
                        <label className='profileScreen__inputLabel' htmlFor="language-select">{t('COMMON_LANGUAGE')}</label>
                        <Select
                            id="language-select"
                            value={formLanguage || selectedDictionary || ""}
                            onChange={(e: React.ChangeEvent<{ value: unknown }>) => setFormLanguage(e.target.value as string)}
                            displayEmpty
                            variant='outlined'
                            className={classes.input}
                        >
                            <MenuItem value="">{t("COMMON_CHOOSE") as string}</MenuItem>
                            {Object.keys(languageCodeToNameMap).map((lang: any) => (
                                <MenuItem key={lang} value={lang}>
                                    {(languageCodeToNameMap as any)[lang]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <label className='profileScreen__inputLabel' htmlFor="word-input">{t('SETTING_CUSTOM_DICTIONARY_WORD')}</label>
                        <OutlinedInput id="word-input" value={formWordInput} onChange={(e) => setFormWordInput(e.target.value)} className={classes.input} />
                    </FormControl>
                    <FormControl fullWidth>
                        <label className='profileScreen__inputLabel' htmlFor="sounds-like-input">{t('SETTING_CUSTOM_DICTIONARY_SOUNDS_LIKE')}</label>
                        <OutlinedInput id="sounds-like-input" multiline maxRows={3} value={formSoundsLike} onChange={(e) => setFormSoundsLike(e.target.value)} className={classes.input} placeholder={t('SETTING_CUSTOM_DICTIONARY_SOUNDS_LIKE_PLACEHOLDER') as string}/>
                    </FormControl>
                </DialogContent>
                <DialogActions className={"dialogActions dialogActions--end"}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveChanges}
                        disabled={!formLanguage || !formWordInput}
                    >
                        {t('COMMON_SAVE_CHANGES')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CustomDictionary;